import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RolesService } from '../../services/roles.service';
import { Role, Permission } from '../../models';
import { RoleDialogComponent } from './role-dialog.component';
import { ConfirmDialogComponent } from '../shared/confirm-dialog.component';

@Component({
  selector: 'app-roles',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatMenuModule,
    MatSnackBarModule,
    MatTooltipModule
  ],
  template: `
    <div class="roles-container">
      <div class="page-header">
        <h1>Gestión de Roles</h1>
        <button mat-raised-button color="primary" (click)="openDialog()">
          <mat-icon>add</mat-icon>
          Nuevo Rol
        </button>
      </div>

      <div class="roles-grid">
        <mat-card *ngFor="let role of roles" class="role-card">
          <mat-card-header>
            <div mat-card-avatar class="role-icon">
              <mat-icon>{{ getRoleIcon(role.name) }}</mat-icon>
            </div>
            <mat-card-title>{{ role.name }}</mat-card-title>
            <mat-card-subtitle>{{ role.usersCount }} usuarios asignados</mat-card-subtitle>
            <button mat-icon-button [matMenuTriggerFor]="menu" class="card-menu">
              <mat-icon>more_vert</mat-icon>
            </button>
            <mat-menu #menu="matMenu">
              <button mat-menu-item (click)="openDialog(role)">
                <mat-icon>edit</mat-icon>
                <span>Editar</span>
              </button>
              <button mat-menu-item (click)="deleteRole(role)" [disabled]="role.usersCount > 0">
                <mat-icon>delete</mat-icon>
                <span>Eliminar</span>
              </button>
            </mat-menu>
          </mat-card-header>

          <mat-card-content>
            <p class="role-description">{{ role.description }}</p>

            <div class="permissions-section">
              <span class="permissions-label">Permisos ({{ role.permissions.length }})</span>
              <div class="permissions-chips">
                <mat-chip *ngFor="let permission of role.permissions.slice(0, 4)">
                  {{ formatPermission(permission) }}
                </mat-chip>
                <mat-chip *ngIf="role.permissions.length > 4" class="more-chip">
                  +{{ role.permissions.length - 4 }} más
                </mat-chip>
              </div>
            </div>
          </mat-card-content>

          <mat-card-actions>
            <button mat-button color="primary" (click)="openDialog(role)">
              <mat-icon>edit</mat-icon>
              Editar Permisos
            </button>
          </mat-card-actions>
        </mat-card>
      </div>
    </div>
  `,
  styles: [`
    .roles-container {
      max-width: 1400px;
      margin: 0 auto;
    }

    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;

      h1 {
        font-family: var(--font-display);
        font-size: 28px;
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--text-primary);
        margin: 0;
      }
    }

    .roles-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
      gap: 24px;
    }

    .role-card {
      position: relative;
      background: var(--surface-color);
      border: 1px solid rgba(0, 0, 0, 0.05);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-sm);
      transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: var(--shadow-md);
      }

      mat-card-header {
        position: relative;
      }

      .card-menu {
        position: absolute;
        top: 8px;
        right: 8px;
      }
    }

    .role-icon {
      width: 40px;
      height: 40px;
      border-radius: var(--radius-md);
      background: rgba(183, 28, 28, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;

      mat-icon {
        color: var(--primary-color);
      }
    }

    .role-description {
      color: var(--text-secondary);
      font-size: 14px;
      margin-bottom: 16px;
    }

    .permissions-section {
      margin-top: 16px;
    }

    .permissions-label {
      font-size: 12px;
      font-weight: 500;
      color: var(--text-secondary);
      text-transform: uppercase;
      display: block;
      margin-bottom: 8px;
    }

    .permissions-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      mat-chip {
        font-size: 11px;
        font-weight: 600;
        border-radius: 999px;
        background: rgba(183, 28, 28, 0.08);
        color: var(--primary-color);
      }

      .more-chip {
        background: rgba(0, 0, 0, 0.08);
        color: var(--text-secondary);
      }
    }

    mat-card-actions {
      padding: 8px 16px 16px;
    }
  `]
})
export class RolesComponent implements OnInit {
  roles: Role[] = [];
  permissions: string[] = [];

  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private rolesService = inject(RolesService);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    this.rolesService.getAllRoles().subscribe(roles => {
      setTimeout(() => {
        this.roles = roles;
        this.cdr.detectChanges();
      }, 0);
    });

    this.rolesService.getAllPermissions().subscribe(permissions => {
      setTimeout(() => {
        this.permissions = permissions.map(p => p.code);
        this.cdr.detectChanges();
      }, 0);
    });
  }

  getRoleIcon(roleName: string): string {
    const icons: Record<string, string> = {
      'Admin': 'admin_panel_settings',
      'Empresa': 'business'
    };
    return icons[roleName] || 'badge';
  }

  formatPermission(permission: string): string {
    const parts = permission.split('.');
    const actions: Record<string, string> = {
      'read': 'Ver',
      'write': 'Editar',
      'delete': 'Eliminar',
      'execute': 'Ejecutar'
    };
    return `${actions[parts[1]] || parts[1]} ${parts[0]}`;
  }

  openDialog(role?: Role) {
    const dialogRef = this.dialog.open(RoleDialogComponent, {
      width: '600px',
      data: { role, permissions: this.permissions }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (role) {
          this.rolesService.updateRole(role.id, {
            name: result.name,
            description: result.description,
            permissionCodes: result.permissions
          }).subscribe(updatedRole => {
            const index = this.roles.findIndex(r => r.id === role.id);
            if (index !== -1) {
              this.roles[index] = updatedRole;
            }
            this.snackBar.open('Rol actualizado correctamente', 'Cerrar', { duration: 3000 });
          });
        } else {
          this.rolesService.createRole({
            name: result.name,
            description: result.description,
            permissionCodes: result.permissions
          }).subscribe(newRole => {
            this.roles.push(newRole);
            this.snackBar.open('Rol creado correctamente', 'Cerrar', { duration: 3000 });
          });
        }
      }
    });
  }

  deleteRole(role: Role) {
    if (role.usersCount > 0) {
      this.snackBar.open('No se puede eliminar un rol con usuarios asignados', 'Cerrar', { duration: 3000 });
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Eliminar Rol',
        message: `¿Estás seguro de que deseas eliminar el rol "${role.name}"?`,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        type: 'danger'
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.rolesService.deleteRole(role.id).subscribe(() => {
          this.roles = this.roles.filter(r => r.id !== role.id);
          this.snackBar.open('Rol eliminado correctamente', 'Cerrar', { duration: 3000 });
        });
      }
    });
  }
}
